// backend.ts — where the app talks to the ReNurture backend (the server/ folder)
// for progress sync + research/PID. Identity, auth and entitlement are Supabase
// now (see config/supabase.ts); Thinkific has been fully retired.
//
// Values come from a GITIGNORED `.env` at the repo root (Vite `VITE_*` vars), so
// no secret is ever committed. Without that .env the app uses the safe fallbacks
// below.
//
// .env keys: VITE_BACKEND_URL, VITE_RENURTURE_APP_SECRET,
// VITE_RESEARCH_CHECKIN_URL, VITE_RESEARCH_PROGRESS_URL.
const env = import.meta.env;

export const BACKEND_URL: string =
  (env.VITE_BACKEND_URL as string) || 'https://renurtureprogrammeapp.replit.app';

// Shared secret sent as `x-api-secret` on protected endpoints. Must equal the
// backend's RENURTURE_APP_SECRET. Empty while the backend runs open.
export const APP_SECRET: string = (env.VITE_RENURTURE_APP_SECRET as string) || '';

// Off-app research check-in (longitudinal impact measurement). The app opens this
// page in the in-app browser with the user's anonymised pid appended, so a
// person's check-ins link over time without any identity leaving the device.
// This is the COMBINED check-in (ONS-4 + GAD-7 + PHQ-8 + WSAS); each sitting is
// saved as a server-assigned wave (001, 002…) so change is tracked over time.
// (GAD-7/PHQ-8 are research-only; the programme's own 6-item lives in-app per Act.)
// (Separate from the daily engagement snapshot — see RESEARCH_PROGRESS_URL.)
// renurture.org serves the Cloudflare site, so the check-in lives on the branded
// domain. Override via VITE_RESEARCH_CHECKIN_URL.
export const RESEARCH_CHECKIN_URL: string =
  (env.VITE_RESEARCH_CHECKIN_URL as string) || 'https://renurture.org/assessment-suite.html';

// The full 24-item FOGS self-check (the public assessment page). The app opens it with
// a return URL (web), consent=1|0, and pid/cohort/token for any signed-in user.
// Override via VITE_FOGS_CHECK_URL.
export const FOGS_CHECK_URL: string =
  (env.VITE_FOGS_CHECK_URL as string) || 'https://www.renurture.org/assessment-fogs';

// Endpoint the app posts a PID-linked engagement snapshot to (counts only — no
// content), so corporate buyers can see programme engagement per organisation.
// Same Cloudflare site as the check-in. Override via VITE_RESEARCH_PROGRESS_URL.
export const RESEARCH_PROGRESS_URL: string =
  (env.VITE_RESEARCH_PROGRESS_URL as string) || 'https://renurture.org/research-progress';

// PID-linked single-instrument measure endpoint (same Cloudflare site as the
// check-in). Used to mirror the end-of-act wellbeing-6 into the research store for
// consented users. Override via VITE_RESEARCH_MEASURE_URL.
export const RESEARCH_MEASURE_URL: string =
  (env.VITE_RESEARCH_MEASURE_URL as string) || 'https://renurture.org/research-measure';

// Organisation access redemption (B2B/EAP). The app POSTs the signed-in user's
// Supabase token + an optional code here; the Worker grants membership directly
// (a code, or a match on the user's verified email DOMAIN) and tags their cohort.
// Same Cloudflare site; has CORS for the app origins. Override via VITE_ORG_REDEEM_URL.
export const ORG_REDEEM_URL: string =
  (env.VITE_ORG_REDEEM_URL as string) || 'https://renurture.org/org-redeem';

// One person's own check-in history (for the in-app "Your progress" view). The
// app GETs this with its pid + token; the Worker returns only that pid's waves.
// Same Cloudflare site; has CORS for the app origins. Override via VITE_RESEARCH_MINE_URL.
export const RESEARCH_MINE_URL: string =
  (env.VITE_RESEARCH_MINE_URL as string) || 'https://renurture.org/research-mine';

// Research consent ledger. When a user grants or withdraws research consent, the
// app POSTs { pid, token, consent } here so the server's record matches the app's
// state. Best-effort. Same Cloudflare site. Override via VITE_RESEARCH_CONSENT_URL.
export const RESEARCH_CONSENT_URL: string =
  (env.VITE_RESEARCH_CONSENT_URL as string) || 'https://renurture.org/research-consent';

// Research/ROM deletion. On account deletion the app POSTs { pid, token } here so
// the user's D1 research/ROM rows are purged alongside the Supabase data. Best-
// effort. Same Cloudflare site. Override via VITE_RESEARCH_DELETE_URL.
export const RESEARCH_DELETE_URL: string =
  (env.VITE_RESEARCH_DELETE_URL as string) || 'https://renurture.org/research-delete';

// Stripe Billing Customer Portal. The app POSTs the Supabase token here and gets
// back { url } to a Stripe-hosted page where the user can view invoices, update
// their card, or CANCEL their subscription. Same Cloudflare site (CORS for app
// origins). Override via VITE_BILLING_PORTAL_URL.
export const BILLING_PORTAL_URL: string =
  (env.VITE_BILLING_PORTAL_URL as string) || 'https://renurture.org/billing-portal';

// Anonymous, aggregate-only engagement telemetry (what content is saved / opened
// / completed / used most). NO pid, NO user link — just per-content counters, so
// it carries no personal data. Same Cloudflare site; has CORS for the app origins.
// Override via VITE_RESEARCH_ENGAGEMENT_URL.
export const RESEARCH_ENGAGEMENT_URL: string =
  (env.VITE_RESEARCH_ENGAGEMENT_URL as string) || 'https://renurture.org/research-engagement';

// Kit sync — after account creation the app POSTs { first_name } here with the
// Supabase token; the Worker (which holds KIT_API_KEY) upserts the user into Kit
// (email + first name) for personalised email. Best-effort. Same Cloudflare site
// (CORS for app origins). Override via VITE_KIT_SYNC_URL.
export const KIT_SYNC_URL: string =
  (env.VITE_KIT_SYNC_URL as string) || 'https://renurture.org/kit-sync';

// Kit forget — on account deletion the app POSTs the user's Supabase token here so the
// Worker unsubscribes them from Kit (removes them from all mailing; GDPR erasure →
// processor). Best-effort. Same Cloudflare site (CORS for app origins).
// Override via VITE_KIT_FORGET_URL.
export const KIT_FORGET_URL: string =
  (env.VITE_KIT_FORGET_URL as string) || 'https://renurture.org/kit-forget';

// "Report a bug" (Settings) — the app POSTs { message, email?, context? } here and
// the Worker emails it to support@renurture.org via Resend, tagged as a bug report.
// Same Cloudflare site; has CORS for the app origins. Override via VITE_BUG_REPORT_URL.
export const BUG_REPORT_URL: string =
  (env.VITE_BUG_REPORT_URL as string) || 'https://renurture.org/app-bug-report';
